import { Flex } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import Icon from '@ant-design/icons';
import styles from './business-hours-day.module.scss';
import { BusinessHoursSwitch } from '../business-hours-switch/business-hours-switch';
import { TimePicker } from '../time-picker/time-picker';
import { Checkbox } from '../checkbox/checkbox';
import MoonIcon from '@/assets/icons/moon-01.svg?react';
import PlusCircleIcon from '@/assets/icons/plus-circle.svg?react';
import TrashIcon from '@/assets/icons/trash-01.svg?react';
import InfoCircleIcon from '@/assets/icons/info-circle.svg?react';
import { Button } from '../button/button';

export type BusinessHoursDayData = {
  weekday: string;
  periods: { start: string; end: string }[];
  isClosed: boolean;
  isAllDay: boolean;
  hasError?: boolean;
};

type Period = {
  start: Dayjs | null;
  end: Dayjs | null;
};

interface BusinessHoursDayProps {
  day: string;
  weekday: string;
  initialStartTime?: string | null;
  initialEndTime?: string | null;
  initialIsClosed?: boolean;
  initialIsAllDay?: boolean;
  initialPeriods?: { start: string; end: string }[];
  onChange?: (data: BusinessHoursDayData) => void;
}

export function BusinessHoursDay({
  day,
  weekday,
  initialStartTime = '08:00',
  initialEndTime = '16:00',
  initialIsClosed = false,
  initialIsAllDay = false,
  initialPeriods,
  onChange,
}: BusinessHoursDayProps) {
  const [isOpen, setIsOpen] = useState(!initialIsClosed);
  const [periods, setPeriods] = useState<Period[]>(() => {
    if (initialPeriods && initialPeriods.length > 0) {
      return initialPeriods.map((p) => ({
        start: dayjs(p.start, 'HH:mm'),
        end: dayjs(p.end, 'HH:mm'),
      }));
    }
    return [
      {
        start: initialStartTime ? dayjs(initialStartTime, 'HH:mm') : dayjs('08:00', 'HH:mm'),
        end: initialEndTime ? dayjs(initialEndTime, 'HH:mm') : dayjs('16:00', 'HH:mm'),
      },
    ];
  });
  const [isAllDay, setIsAllDay] = useState(initialIsAllDay);

  const hasSameTime = useMemo(() => {
    return periods.some((period) => {
      if (!period.start || !period.end) return false;
      return period.start.isSame(period.end);
    });
  }, [periods]);

  const validatePeriods = useMemo(() => {
    type ErrorType = 'endBeforeStart' | 'overlap';
    const errors: {
      index: number;
      startInvalid: boolean;
      endInvalid: boolean;
      type: ErrorType;
    }[] = [];

    for (let i = 0; i < periods.length; i++) {
      const { start, end } = periods[i];

      if (start && end && end.isBefore(start)) {
        errors.push({ index: i, startInvalid: false, endInvalid: true, type: 'endBeforeStart' });
        continue;
      }

      if (i > 0) {
        const prevEnd = periods[i - 1].end;
        const startInvalid = !!(prevEnd && start && start.isBefore(prevEnd));
        const endInvalid = !!(prevEnd && end && end.isBefore(prevEnd));

        if (startInvalid || endInvalid) {
          errors.push({ index: i, startInvalid, endInvalid, type: 'overlap' });
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [periods]);

  useEffect(() => {
    const formattedPeriods =
      isOpen && !isAllDay
        ? periods
            .filter((p) => p.start && p.end)
            .map((p) => ({
              start: p.start!.format('HH:mm'),
              end: p.end!.format('HH:mm'),
            }))
        : [];

    onChange?.({
      weekday,
      periods: formattedPeriods,
      isClosed: !isOpen,
      isAllDay: isOpen && isAllDay,
      hasError: isOpen && !isAllDay && !validatePeriods.isValid,
    });
  }, [isOpen, periods, isAllDay, weekday, onChange, validatePeriods.isValid]);

  const handleSwitchChange = (checked: boolean) => {
    setIsOpen(checked);
    if (!checked) {
      setIsAllDay(false);
    }
  };

  const handleAllDayChange = (checked: boolean) => {
    setIsAllDay(checked);
  };

  const handlePeriodChange = (index: number, field: 'start' | 'end', value: Dayjs | null) => {
    const newPeriods = [...periods];
    newPeriods[index] = { ...newPeriods[index], [field]: value };
    setPeriods(newPeriods);
  };

  const handleAddPeriod = () => {
    const lastPeriod = periods[periods.length - 1];
    const newStart = lastPeriod.end ? lastPeriod.end.add(1, 'hour') : dayjs('18:00', 'HH:mm');
    const newEnd = newStart.add(2, 'hour');
    setPeriods([...periods, { start: newStart, end: newEnd }]);
  };

  const handleRemovePeriod = (index: number) => {
    setPeriods(periods.filter((_, i) => i !== index));
  };

  return (
    <Flex className={styles.dayRow} justify="space-between">
      <Flex className={styles.switchCheckboxContainer}>
        <Flex gap={12} className={styles.dayLabel}>
          <BusinessHoursSwitch onChange={handleSwitchChange} defaultChecked={isOpen} />
          <p className={styles.day}>{day}</p>
        </Flex>
        <Checkbox
          disabled={!isOpen}
          checked={isAllDay}
          onChange={(e) => handleAllDayChange(e.target.checked)}
        >
          All day
        </Checkbox>
      </Flex>

      {isOpen ? (
        <Flex vertical gap={8} className={styles.timeSection}>
          {periods.map((period, index) => {
            const error = validatePeriods.errors.find((e) => e.index === index);
            const hasStartError = error?.startInvalid ?? false;
            const hasEndError = error?.endInvalid ?? false;
            const isFirstRow = index === 0;

            return (
              <Flex key={index} gap={8} className={styles.timePickersContainer}>
                <Flex vertical gap={4} className={styles.timePickerWrapper}>
                  <TimePicker
                    value={period.start}
                    onChange={(value) => handlePeriodChange(index, 'start', value)}
                    disabled={isAllDay}
                    placeholder="08:00 AM"
                    status={hasStartError ? 'error' : undefined}
                  />
                </Flex>
                <span className={styles.separator}>-</span>
                <Flex vertical gap={4} className={styles.timePickerWrapper}>
                  <TimePicker
                    value={period.end}
                    onChange={(value) => handlePeriodChange(index, 'end', value)}
                    disabled={isAllDay}
                    placeholder="04:00 PM"
                    status={hasEndError ? 'error' : undefined}
                  />
                </Flex>
                <Flex className={styles.actions} align="center">
                  {isFirstRow ? (
                    <Button
                      buttonVariant="tertiary"
                      className={styles.addButton}
                      onClick={handleAddPeriod}
                      disabled={isAllDay}
                    >
                      <Icon component={() => <PlusCircleIcon className={styles.plusIcon} />} />
                    </Button>
                  ) : (
                    <Button
                      buttonVariant="tertiary"
                      className={styles.removeButton}
                      onClick={() => handleRemovePeriod(index)}
                      disabled={isAllDay}
                    >
                      <Icon component={() => <TrashIcon className={styles.trashIcon} />} />
                    </Button>
                  )}
                </Flex>
              </Flex>
            );
          })}
          {validatePeriods.errors.some((e) => e.type === 'endBeforeStart') && (
            <p className={styles.errorText}>End time cannot be earlier than start time.</p>
          )}
          {validatePeriods.errors.some((e) => e.type === 'overlap') && (
            <p className={styles.errorText}>
              Time ranges cannot overlap. Each period must start after the previous one ends.
            </p>
          )}
          {hasSameTime && !validatePeriods.errors.length && (
            <Flex gap={8} align="center" className={styles.infoText}>
              <Icon component={() => <InfoCircleIcon className={styles.infoIcon} />} />
              <span>Setting the same start and end time will mark this day as closed.</span>
            </Flex>
          )}
        </Flex>
      ) : (
        <Flex className={styles.closedSection} align="center" gap={8}>
          <Icon component={() => <MoonIcon className={styles.moonIcon} />} />
          <span className={styles.closedText}>Closed</span>
        </Flex>
      )}
    </Flex>
  );
}
